// packages/ui/src/components/FileUploadInput.tsx
//
// Canonical single-file upload widget. Replaces the "paste a CDN URL into
// a text field" pattern scattered across admin / merchant / vendor portals
// (cms-help, cms-homepage, products/CategoryDetailPanel, home-management,
// legal-entities, settings/GeneralTab, …).
//
// Every upload across the platform now flows through one endpoint:
//   POST /api/v1/files/upload  (multipart/form-data, X-XSRF-TOKEN required)
//
// The component handles:
//   - CSRF double-submit cookie → X-XSRF-TOKEN header.
//   - Cookie credentials (`credentials: 'include'`).
//   - Server response unwrap → `{ id, url }` invariant for callers.
//   - Preview thumbnail when the existing value is a known image URL.
//   - Replace + Remove buttons so the form is self-contained.
//
// dbg-admin-upload-standardize-2026-05-23.

import { useRef, useState, type JSX } from 'react';

export interface FileUploadInputResult {
  /** 24-hex Mongo ObjectId returned by the file service. */
  id: string;
  /** Absolute public URL served by `GET /api/v1/files/:id`. */
  url: string;
}

export interface FileUploadInputProps {
  /** Current value (URL). Empty string when no file selected yet. */
  value: string;
  /** Fired with the new public URL once upload completes successfully. */
  onChange: (url: string, meta?: FileUploadInputResult) => void;
  /** API base — must NOT include a trailing slash. Required because the
   *  three portals each ship their own `import.meta.env.VITE_API_BASE_URL`. */
  apiBaseUrl: string;
  /** Accepted MIME types — narrows the OS file picker. Defaults to images. */
  accept?: string;
  /** Maximum upload size in bytes. Defaults to 5 MB. */
  maxBytes?: number;
  /** Render label above the picker (optional). */
  label?: string;
  /** Helper text under the picker (optional). */
  helperText?: string;
  /** Forwarded to the file input for E2E test addressing. */
  testId?: string;
  /** Disable the picker (read-only / submitting). */
  disabled?: boolean;
  /** Show a preview thumbnail when value is set. Defaults to true. */
  showPreview?: boolean;
}

interface UploadEnvelope {
  success?: boolean;
  data?: { file_id?: string; url?: string };
  file_id?: string;
  url?: string;
  error?: { code?: string; message?: string };
}

function readCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]*)/);
  return m && typeof m[1] === 'string' ? decodeURIComponent(m[1]) : null;
}

export function FileUploadInput({
  value,
  onChange,
  apiBaseUrl,
  accept = 'image/png,image/jpeg,image/webp,image/gif,image/svg+xml',
  maxBytes = 5 * 1024 * 1024,
  label,
  helperText,
  testId,
  disabled,
  showPreview = true,
}: FileUploadInputProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const previewSrc = showPreview && value && /^https?:\/\//.test(value) ? value : null;

  function reset(): void {
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  async function handleFiles(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file) return;
    if (file.size > maxBytes) {
      setError(
        `File quá lớn — tối đa ${(maxBytes / 1024 / 1024).toFixed(1)} MB`,
      );
      reset();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const csrf = readCsrfToken();
      const res = await fetch(`${apiBaseUrl}/api/v1/files/upload`, {
        method: 'POST',
        credentials: 'include',
        headers: csrf ? { 'X-XSRF-TOKEN': csrf } : undefined,
        body: fd,
      });
      if (!res.ok) {
        const text = await res.text();
        setError(text || `Upload thất bại (HTTP ${res.status})`);
        return;
      }
      const body = (await res.json()) as UploadEnvelope;
      const fileId = body?.data?.file_id ?? body?.file_id ?? '';
      if (!fileId) {
        setError('Upload thất bại — server không trả file_id');
        return;
      }
      const url = `${apiBaseUrl}/api/v1/files/${fileId}`;
      onChange(url, { id: String(fileId), url });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload thất bại');
    } finally {
      setBusy(false);
      reset();
    }
  }

  return (
    <div className="flex flex-col gap-2" data-testid={testId ?? 'file-upload-input'}>
      {label ? (
        <span className="text-sm font-medium text-pw-text">{label}</span>
      ) : null}
      {previewSrc ? (
        <div className="flex items-center gap-3">
          <img
            src={previewSrc}
            alt={label ?? 'preview'}
            className="h-16 w-16 rounded border border-pw-border object-cover"
          />
          <div className="flex flex-col gap-1">
            <span className="font-mono text-xs text-pw-text-muted">
              {previewSrc.slice(-32)}
            </span>
            <div className="flex gap-2 text-xs">
              <button
                type="button"
                disabled={disabled || busy}
                onClick={() => inputRef.current?.click()}
                className="rounded border border-pw-border bg-pw-bg px-2 py-1 hover:bg-pw-surface disabled:opacity-50"
                data-testid={`${testId ?? 'file-upload-input'}-replace`}
              >
                {busy ? 'Đang tải…' : 'Thay file'}
              </button>
              <button
                type="button"
                disabled={disabled || busy}
                onClick={() => onChange('')}
                className="rounded border border-rose-200 bg-rose-50 px-2 py-1 text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                data-testid={`${testId ?? 'file-upload-input'}-remove`}
              >
                Gỡ
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() => inputRef.current?.click()}
          className="inline-flex w-fit items-center gap-2 rounded-md border border-dashed border-pw-border bg-pw-bg px-3 py-2 text-sm hover:bg-pw-surface disabled:opacity-50"
          data-testid={`${testId ?? 'file-upload-input'}-trigger`}
        >
          {busy ? 'Đang tải…' : 'Chọn file…'}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        disabled={disabled || busy}
        onChange={(e) => void handleFiles(e.target.files)}
        className="hidden"
        data-testid={`${testId ?? 'file-upload-input'}-native`}
      />
      {helperText ? (
        <p className="text-xs text-pw-text-muted">{helperText}</p>
      ) : null}
      {error ? <p className="text-xs text-rose-600">{error}</p> : null}
    </div>
  );
}
